/**
 * InvestigationsPage (Phase-2, Module 6)
 *
 * Officer requests investigations (lab tests), tracks the workflow, and views
 * the timeline. Lab assignment + report upload happen on the lab/admin side.
 *
 * @author Civilhealth Development Team
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  MenuItem, FormControl, InputLabel, Select, IconButton, Tooltip, Chip, Typography, Divider, Stack,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Biotech as InvIcon, Add as AddIcon, Visibility as ViewIcon } from '@mui/icons-material';
import { PageScaffold, StatusBadge, OfficerSelector } from '../../components/common';
import { InvestigationApi, toAbsoluteFileUrl } from '../../services/phase2Api';
import { TEST_TYPES, labelFor } from '../../utils/phase2Constants';
import type { InvestigationRequest as InvRequest, InvestigationTimelineEvent, Laboratory } from '../../types/phase2';
import { LaboratoryApi } from '../../services/phase2Api';
import { useCurrentOfficer } from '../../hooks/useCurrentOfficer';
import { useNavigate, useLocation } from 'react-router-dom';

const PRIORITIES = ['LOW', 'NORMAL', 'HIGH', 'URGENT'];

const InvestigationsPage: React.FC = () => {
  const { officerId } = useCurrentOfficer();
  const navigate = useNavigate();
  const location = useLocation();
  const [rows, setRows] = useState<InvRequest[]>([]);
  const [labs, setLabs] = useState<Laboratory[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState<InvRequest | null>(null);
  const [form, setForm] = useState({
    patientName: '', testType: 'BLOOD_TEST', testName: '', testDescription: '',
    labId: '' as number | '', priority: 'NORMAL', scheduledDate: '', notes: '',
  });

  const load = useCallback(async () => {
    if (!officerId) return;
    setLoading(true); setError(null);
    try {
      const [data, activeLabs] = await Promise.all([InvestigationApi.listByOfficer(officerId), LaboratoryApi.getActive()]);
      setRows(data || []); setLabs(activeLabs || []);
    } catch (e: any) { setError(e?.response?.data?.message || 'Failed to load investigations'); }
    finally { setLoading(false); }
  }, [officerId]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    if (!officerId || !form.testType) return;
    setSaving(true); setError(null);
    try {
      await InvestigationApi.create({
        officerId, patientName: form.patientName || undefined, testType: form.testType,
        testName: form.testName || undefined, testDescription: form.testDescription || undefined,
        labId: form.labId ? Number(form.labId) : undefined,
        labName: labs.find((l) => l.id === Number(form.labId))?.labName,
        priority: form.priority, scheduledDate: form.scheduledDate || undefined, notes: form.notes || undefined,
      });
      setOpen(false);
      setForm({ patientName: '', testType: 'BLOOD_TEST', testName: '', testDescription: '', labId: '', priority: 'NORMAL', scheduledDate: '', notes: '' });
      load();
    } catch (e: any) { setError(e?.response?.data?.message || 'Failed to create investigation'); }
    finally { setSaving(false); }
  };

  const columns: GridColDef[] = [
    { field: 'investigationNumber', headerName: 'Inv. No.', width: 150 },
    { field: 'testType', headerName: 'Test Type', width: 140, renderCell: (p) => <Chip size="small" label={labelFor(TEST_TYPES, p.value)} variant="outlined" /> },
    { field: 'testName', headerName: 'Test', flex: 1.2, minWidth: 150, renderCell: (p) => p.value || p.row.customTestName || '—' },
    { field: 'labName', headerName: 'Lab', width: 150, renderCell: (p) => p.value || '—' },
    { field: 'priority', headerName: 'Priority', width: 110 },
    { field: 'status', headerName: 'Status', width: 150, renderCell: (p) => <StatusBadge status={p.value} size="small" /> },
    {
      field: 'actions', headerName: 'Actions', width: 110, sortable: false,
      renderCell: (p) => (
        <Box>
          <Tooltip title="View"><IconButton size="small" onClick={() => setDetail(p.row)}><ViewIcon fontSize="small" /></IconButton></Tooltip>
          {p.row.reportUrl && <Tooltip title="Report"><IconButton size="small" href={toAbsoluteFileUrl(p.row.reportUrl)} target="_blank" rel="noopener noreferrer"><InvIcon fontSize="small" /></IconButton></Tooltip>}
        </Box>
      ),
    },
  ];

  return (
    <PageScaffold title="Investigations" subtitle="Request & track lab investigations" icon={<InvIcon />}
      actionLabel={officerId ? 'Request Investigation' : undefined} actionIcon={<AddIcon />} onAction={officerId ? () => setOpen(true) : undefined}
      loading={loading && rows.length === 0} error={error} onDismissError={() => setError(null)}>
      {!officerId && (
        <OfficerSelector onSelect={(id) => navigate(`${location.pathname}?officerId=${id}`)} />
      )}
      {officerId && (
      <>
      <Box sx={{ height: 520, bgcolor: '#fff', borderRadius: 3, border: '1px solid #E5E7EB' }}>
        <DataGrid rows={rows} columns={columns} getRowId={(r) => r.id}
          pageSizeOptions={[10, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }} disableRowSelectionOnClick />
      </Box>
      </>
      )}

      <Dialog open={open} onClose={() => !saving && setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700 }}>Request Investigation</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Test Type</InputLabel>
              <Select label="Test Type" value={form.testType} onChange={(e) => setForm({ ...form, testType: e.target.value })}>
                {TEST_TYPES.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField label="Test Name" value={form.testName} onChange={(e) => setForm({ ...form, testName: e.target.value })} fullWidth />
            <TextField label="Patient Name" value={form.patientName} onChange={(e) => setForm({ ...form, patientName: e.target.value })} fullWidth />
            <TextField label="Test Description / Reason" multiline minRows={2} value={form.testDescription} onChange={(e) => setForm({ ...form, testDescription: e.target.value })} fullWidth />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Preferred Lab</InputLabel>
                <Select label="Preferred Lab" value={form.labId} onChange={(e) => setForm({ ...form, labId: Number(e.target.value) })}>
                  <MenuItem value="">Any</MenuItem>
                  {labs.map((l) => <MenuItem key={l.id} value={l.id}>{l.labName}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select label="Priority" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
                  {PRIORITIES.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                </Select>
              </FormControl>
            </Box>
            <TextField type="date" label="Scheduled Date" InputLabelProps={{ shrink: true }} value={form.scheduledDate} onChange={(e) => setForm({ ...form, scheduledDate: e.target.value })} fullWidth />
            <TextField label="Notes" multiline minRows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={!officerId || saving} sx={{ textTransform: 'none', fontWeight: 600 }}>{saving ? 'Submitting...' : 'Submit'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!detail} onClose={() => setDetail(null)} fullWidth maxWidth="md">
        <DialogTitle sx={{ fontWeight: 700 }}>{detail?.investigationNumber}</DialogTitle>
        <DialogContent dividers>
          {detail && (
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Chip label={`Type: ${labelFor(TEST_TYPES, detail.testType)}`} variant="outlined" />
                <Chip label={`Status: ${detail.statusDisplayName || detail.status}`} color="primary" />
                {detail.labName && <Chip label={`Lab: ${detail.labName}`} variant="outlined" />}
                {detail.priority && <Chip label={`Priority: ${detail.priority}`} variant="outlined" />}
              </Box>
              {detail.testDescription && <Typography variant="body2"><strong>Description:</strong> {detail.testDescription}</Typography>}
              {detail.results && <Typography variant="body2"><strong>Results:</strong> {detail.results}</Typography>}
              {detail.reportUrl && <Button size="small" href={toAbsoluteFileUrl(detail.reportUrl)} target="_blank" rel="noopener noreferrer">View Report</Button>}
              <Divider><Typography variant="caption">TIMELINE</Typography></Divider>
              <Stack spacing={1}>
                {(detail.timeline || []).map((t: InvestigationTimelineEvent, i: number) => (
                  <Box key={i} sx={{ borderLeft: '3px solid #8B5CF6', pl: 1.5 }}>
                    <Typography variant="body2" fontWeight={600}>{t.eventTitle}</Typography>
                    <Typography variant="caption" color="text.secondary">{t.eventDate} · {t.performedByName}</Typography>
                    {t.eventDescription && <Typography variant="body2">{t.eventDescription}</Typography>}
                  </Box>
                ))}
                {(!detail.timeline || detail.timeline.length === 0) && <Typography variant="body2" color="text.secondary">No timeline events yet.</Typography>}
              </Stack>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}><Button onClick={() => setDetail(null)}>Close</Button></DialogActions>
      </Dialog>
    </PageScaffold>
  );
};

export default InvestigationsPage;